/* eslint-disable complexity */
import { FieldBase } from '@/components/form/fieldBase';
import { useFieldContext } from '@/components/form/formHooks';
import { FieldProps } from '@/components/form/types';
import { TimeInput } from '@/components/Input/TimeInput';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@e2n/ui';
import { MaskitoOptions, maskitoUpdateElement } from '@maskito/core';
import { maskitoEventHandler } from '@maskito/kit';
import { useMaskito } from '@maskito/react';

// eslint-disable-next-line max-lines-per-function
export function FieldHoursInput({
  label,
  description,
  placeholder,
  isRequired,
  className,
  suffix,
  maxHours,
  maxLength,
  ...inputProps
}: FieldProps &
  React.ComponentProps<typeof TimeInput> & {
    suffix?: string;
    maxHours?: number;
  }) {
  const maxHourDigits = maxLength ?? 4;
  const maxInputLength = maxHourDigits + 3;

  const options: MaskitoOptions = {
    mask: new RegExp(
      `^$|^\\d{1,${maxHourDigits}}$|^\\d{1,${maxHourDigits}}:$|^\\d{1,${maxHourDigits}}:\\d$|^\\d{1,${maxHourDigits}}:[0-5]\\d$`,
    ),
    plugins: [
      maskitoEventHandler('input', (element) => {
        if (element.value.includes(':')) return;
        const hours = element.value.replace(/\D/g, '');
        if (hours.length !== maxHourDigits) return;
        maskitoUpdateElement(element, `${hours}:`);
      }),
      maskitoEventHandler('blur', (element) => {
        const [hours = '', minutes = ''] = element.value.split(':');
        if (!hours) return;
        if (maxHours && Number(hours) > maxHours) {
          maskitoUpdateElement(element, `${maxHours}:00`);
          return;
        }
        const normalizedMinutes =
          minutes === ''
            ? '00'
            : minutes.length === 1 && Number(minutes) < 6
              ? minutes.padEnd(2, '0')
              : minutes.length === 1 && Number(minutes) >= 6
                ? minutes.padStart(2, '0')
                : minutes;
        const normalizedHours =
          maxHourDigits === 2 ? hours.padStart(2, '0') : hours;

        maskitoUpdateElement(
          element,
          `${normalizedHours}:${normalizedMinutes}`,
        );
      }),
    ],
  };
  const field = useFieldContext<string | undefined>();
  const maskito = useMaskito({ options });

  return (
    <FieldBase
      label={label}
      description={description}
      isRequired={isRequired}
      className={className}>
      <InputGroup>
        <InputGroupInput
          id={field.name}
          name={field.name}
          ref={maskito}
          defaultValue={field.state.value ?? ''}
          onBlur={(e) => {
            field.handleChange(e.target.value);
            field.handleBlur();
          }}
          placeholder={placeholder ?? '--:--'}
          maxLength={maxInputLength}
          type="text"
          {...inputProps}
        />
        {suffix && (
          <InputGroupAddon
            className="text-neutrals-700 text-sm font-normal leading-5"
            align={'inline-end'}>
            {suffix}
          </InputGroupAddon>
        )}
      </InputGroup>
    </FieldBase>
  );
}
